import { z } from "zod";

import { HelpSection } from "app/components/HelpDrawer/HelpSection";
import {
  type FilterElementInput,
  type FilterValueInput,
} from "app/utils/filters";

import type { CriteriaFilterValue } from "@stacklet/ui";

export const RULES_PAGE_SIZE = 5;

export const NOTIFICATION_RULE_STEPS = [
  { id: "filters", label: "Filters" },
  { id: "delivery", label: "Delivery Method" },
];

export const NOTIFICATION_RULE_STEP_IDS = NOTIFICATION_RULE_STEPS.map(
  (step) => step.id,
);

const filterValueInputSchema: z.ZodType<FilterValueInput> = z.object({
  name: z.string(),
  operator: z.string().optional().nullable(),
  value: z.string().optional().nullable(),
});

const filterElementInputSchema: z.ZodType<FilterElementInput> = z.object({
  single: filterValueInputSchema.nullish(),
  multiple: z
    .object({
      operator: z.enum(["AND", "OR", "NOT"]),
      operands: z.array(z.lazy(() => filterElementInputSchema)),
    })
    .nullish(),
});

const criteriaFilterValueSchema: z.ZodType<CriteriaFilterValue> = z.object({
  criteria: z.string(),
  name: z.string(),
  operator: z.string().optional(),
  value: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .optional(),
});

export const criteriaFilterValueArraySchema = z.array(
  criteriaFilterValueSchema,
);

export const RULES_FILTERS_HELP_CONTENT = (
  <>
    <HelpSection title="Overview">
      <p>
        Create a notification rule to automatically create Jira issues and
        assign them to resource owners.
      </p>
    </HelpSection>
    <HelpSection title="Resource Criteria">
      <p>
        Select a set set of filters to filter resources by account, resource
        tag, resource type, or account attributes to help narrow down the list
        of resources for notification recipients.
      </p>
    </HelpSection>
    <HelpSection title="Filters">
      <p>
        Select a set of filters based on policy metadata, including policy name,
        policy collection, or binding.
      </p>
      <p>
        <b>Policy Metadata: </b> Use a jmespath to include resource matches
        based on policy metadata.
      </p>
      <p>
        <b>Category:</b> Select a resource match by policy category
      </p>
      <p>
        <b>Severity:</b> Select a resource match by policy severity
      </p>
    </HelpSection>
  </>
);

export const RULES_DELIVERY_METHOD_HELP_CONTENT = (
  <>
    <HelpSection title="Overview">
      <p>
        Configure how Stacklet creates notifications for new resource matches.
      </p>
    </HelpSection>
    <HelpSection title="Jira">
      <p>Use Stacklet to automatically create Jira issues.</p>
      <p>
        Stacklet will first group resource matches by issue assignee. You can
        select auto assignee from one of the following options:
      </p>
      <ul className="list-inside list-disc pl-lg">
        <li>Resource Owner</li>
        <li>Account Owner</li>
        <li>Account Variable</li>
        <li>Manual Value</li>
        <li>No Assignee</li>
      </ul>
      <p>
        <b>Resource Owner</b> and <b>Account Owner</b> are determined by your
        resource owner tag configuration.
      </p>
      <p>
        Account Variable uses account level variables to determine the issue
        assignee.
      </p>
      <p>Manual Value uses a user-supplied custom assignee value.</p>
      <p>
        No Assignee will create an issue without an assignee and will not group
        resources.
      </p>
      <p>
        Next, Stacklet will group resource matches based on the following
        groups:
      </p>
      <ul className="list-inside list-disc pl-lg">
        <li>Policy, Account, and Resource Tag</li>
        <li>Policy and Account</li>
        <li>Policy</li>
      </ul>
      <p>
        Policy, Account, and Resource Tag will result in the fewest issues
        created. Resource matches will be grouped together by the Policy,
        Account, and a user supplied Resource Tag. For resources missing the
        tag, they will be grouped in an issue for each Policy and Account.
      </p>
    </HelpSection>
  </>
);
